/* collection-ajax.js
 * Modern PJAX-style filter updates for the brutalist collection page.
 *
 * On click of a sidebar / chip-bar / mobile-drawer / sort link, we
 * intercept, fetch the destination URL, parse it, and swap the relevant
 * regions in place. URL is updated via history.pushState so back/forward
 * still work. Pagination is left as a normal full reload so the browser
 * scrolls naturally and Metafizzy infinite-scroll can re-init from a
 * clean state — see PAGINATION note below.
 *
 * All swapped HTML originates from the same Shopify origin (same-origin
 * fetch) and is moved as DOM nodes via adoptNode + replaceChildren — we
 * never assign untrusted innerHTML.
 */
(function () {
  'use strict';

  if (window.__collectionAjaxBooted) return;
  window.__collectionAjaxBooted = true;

  /* ---------- Selectors we OWN. Strict allowlist. ---------- */
  var INTERCEPT_CONTAINERS = [
    '.brutal-sidebar',
    '.brutal-active-bar',
    '#mobile-collection-filters'
  ];
  /* Sort dropdown items are <button>s with data-action="select-value".
   * Theme.min.js handles them via _sortByChanged → full reload.
   * We hijack ONLY the ones inside the sort toolbar (not page-size). */
  var SORT_BUTTON_SELECTOR =
    '.collection__toolbar-item--sort .value-picker__choice-item[data-action="select-value"]';
  /* PAGINATION choice: full reload. The Metafizzy infinite-scroll lib
   * already manages "next page" via its own AJAX path, and a manual
   * page-N click benefits from a clean scroll-to-top + history entry.
   * Intercepting here adds little value and a lot of edge cases. */

  var SECTION_SELECTOR    = 'section[data-section-type="collection"]';
  var SIDEBAR_SELECTOR    = '.brutal-sidebar';
  var ACTIVE_BAR_SELECTOR = '.brutal-active-bar';
  var GRID_SELECTOR       = '.brutal-product-grid';
  var DRAWER_INNER        = '#mobile-collection-filters .drawer__inner';
  var TOOLBAR_SELECTOR    = '.collection__toolbar';
  var PAGINATION_SELECTOR = '.brutal-pagination';
  var RESULTS_COUNT       = '.brutal-sidebar__results';

  var BODY_LOADING_ATTR   = 'data-ajax-loading';
  var PROGRESS_VAR        = '--collection-ajax-progress';

  var inFlight = null;

  /* ---------- Click delegation ---------- */
  document.addEventListener('click', function (e) {
    if (e.defaultPrevented) return;
    if (e.button !== 0) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    var target = e.target;
    if (!target || target.nodeType !== 1) return;

    /* Sort dropdown: a <button>, not an <a>. Synthesize URL ourselves. */
    var sortBtn = target.closest && target.closest(SORT_BUTTON_SELECTOR);
    if (sortBtn) {
      var value = sortBtn.getAttribute('data-value');
      if (!value) return;
      e.preventDefault();
      e.stopPropagation();
      go(buildSortUrl(value), { fromDrawer: false });
      return;
    }

    /* Anchor click inside one of our allowlisted containers. */
    var link = target.closest && target.closest('a[href]');
    if (!link) return;
    if (link.target === '_blank') return;

    var inAllowlist = false;
    var fromDrawer  = false;
    for (var i = 0; i < INTERCEPT_CONTAINERS.length; i++) {
      var container = link.closest(INTERCEPT_CONTAINERS[i]);
      if (container) {
        inAllowlist = true;
        if (INTERCEPT_CONTAINERS[i] === '#mobile-collection-filters') fromDrawer = true;
        break;
      }
    }
    if (!inAllowlist) return;

    var href = link.getAttribute('href');
    if (!href || href.charAt(0) === '#') return;
    if (/^(mailto:|tel:|javascript:)/i.test(href)) return;

    var url;
    try { url = new URL(link.href, window.location.href); }
    catch (err) { return; }
    if (url.origin !== window.location.origin) return;

    e.preventDefault();
    go(url.pathname + url.search + url.hash, { fromDrawer: fromDrawer });
  }, true);

  /* ---------- popstate (back/forward) ---------- */
  window.addEventListener('popstate', function () {
    go(window.location.pathname + window.location.search + window.location.hash,
       { fromPopstate: true });
  });

  /* ---------- Core fetch + swap ---------- */
  function go(url, opts) {
    opts = opts || {};
    if (inFlight) inFlight.abort();
    inFlight = (typeof AbortController !== 'undefined') ? new AbortController() : null;

    setLoading(true);

    fetch(url, {
      headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'text/html' },
      credentials: 'same-origin',
      signal: inFlight ? inFlight.signal : undefined
    })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var newGrid = doc.querySelector(GRID_SELECTOR);
        if (!newGrid) throw new Error('Response missing product grid');

        if (!opts.fromPopstate) {
          history.pushState({ ajaxFilter: true, url: url }, '', url);
        }

        swapRegions(doc, opts);
        reinitAfterSwap();
        setLoading(false);
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        /* Hard fallback: native navigation so the user isn't stuck. */
        setLoading(false);
        window.location.href = url;
      });
  }

  /* Move all child nodes of `from` into `into`, replacing `into`'s
   * existing children. Uses adoptNode so the new nodes lose their
   * association with the parsed document and become live in ours. */
  function moveChildren(from, into) {
    while (into.firstChild) into.removeChild(into.firstChild);
    if (!from) return;
    var node;
    while ((node = from.firstChild)) {
      into.appendChild(document.adoptNode(node));
    }
  }

  function adopt(node) {
    return node ? document.adoptNode(node) : null;
  }

  /* ---------- Region swap ---------- */
  function swapRegions(newDoc, opts) {
    var prevActiveSelector = activeElementSelector(document.activeElement);

    /* SIDEBAR — preserve scrollTop on the outer element, replace children. */
    var oldSidebar = document.querySelector(SIDEBAR_SELECTOR);
    var newSidebar = newDoc.querySelector(SIDEBAR_SELECTOR);
    if (oldSidebar) {
      var savedScroll = oldSidebar.scrollTop;
      moveChildren(newSidebar, oldSidebar);
      oldSidebar.scrollTop = savedScroll;
    }

    /* ACTIVE-BAR — present only when filters active. */
    var oldBar = document.querySelector(ACTIVE_BAR_SELECTOR);
    var newBar = newDoc.querySelector(ACTIVE_BAR_SELECTOR);
    if (newBar && oldBar) {
      oldBar.parentNode.replaceChild(adopt(newBar), oldBar);
    } else if (newBar && !oldBar) {
      var grid = document.querySelector(GRID_SELECTOR);
      if (grid && grid.parentNode) grid.parentNode.insertBefore(adopt(newBar), grid);
    } else if (!newBar && oldBar) {
      oldBar.parentNode && oldBar.parentNode.removeChild(oldBar);
    }

    /* PRODUCT GRID — full element replace. */
    var oldGrid = document.querySelector(GRID_SELECTOR);
    var newGrid = newDoc.querySelector(GRID_SELECTOR);
    if (oldGrid && newGrid) {
      oldGrid.parentNode.replaceChild(adopt(newGrid), oldGrid);
    }

    /* PAGINATION — replace whole nav so page numbers update. */
    var oldPg = document.querySelector(PAGINATION_SELECTOR);
    var newPg = newDoc.querySelector(PAGINATION_SELECTOR);
    if (oldPg && newPg) {
      oldPg.parentNode.replaceChild(adopt(newPg), oldPg);
    } else if (!oldPg && newPg) {
      var gridEl = document.querySelector(GRID_SELECTOR);
      if (gridEl && gridEl.parentNode) {
        gridEl.parentNode.insertBefore(adopt(newPg), gridEl.nextSibling);
      }
    } else if (oldPg && !newPg) {
      oldPg.parentNode && oldPg.parentNode.removeChild(oldPg);
    }

    /* MOBILE DRAWER — replace inner only so drawer open/close state
     * (managed by theme.min.js on the outer .drawer) survives. The
     * drawer stays open if the customer is filtering from inside it. */
    var oldDrawer = document.querySelector(DRAWER_INNER);
    var newDrawer = newDoc.querySelector(DRAWER_INNER);
    if (oldDrawer) {
      moveChildren(newDrawer, oldDrawer);
    }

    /* TOOLBAR — sort label + count update. */
    var oldToolbar = document.querySelector(TOOLBAR_SELECTOR);
    var newToolbar = newDoc.querySelector(TOOLBAR_SELECTOR);
    if (oldToolbar && newToolbar) {
      oldToolbar.parentNode.replaceChild(adopt(newToolbar), oldToolbar);
    }

    /* Page <title> for parity with normal navigation. */
    var newTitle = newDoc.querySelector('title');
    if (newTitle) document.title = newTitle.textContent;

    /* Restore focus. */
    if (prevActiveSelector) {
      var restored = document.querySelector(prevActiveSelector);
      if (restored && typeof restored.focus === 'function') {
        try { restored.focus({ preventScroll: true }); } catch (e) { restored.focus(); }
      } else {
        var results = document.querySelector(RESULTS_COUNT);
        if (results) {
          if (!results.hasAttribute('tabindex')) results.setAttribute('tabindex', '-1');
          try { results.focus({ preventScroll: true }); } catch (e) { results.focus(); }
        }
      }
    }
  }

  /* Build a stable selector for the focused element so we can find it
   * again in the new DOM. id > data-value > href. */
  function activeElementSelector(el) {
    if (!el || el === document.body) return null;
    if (el.id) return '#' + cssEscape(el.id);
    var v = el.getAttribute && el.getAttribute('data-value');
    if (v) return el.tagName.toLowerCase() + '[data-value="' + cssEscape(v) + '"]';
    var h = el.getAttribute && el.getAttribute('href');
    if (h) return 'a[href="' + cssEscape(h) + '"]';
    return null;
  }
  function cssEscape(s) {
    if (window.CSS && CSS.escape) return CSS.escape(s);
    return String(s).replace(/(["\\])/g, '\\$1');
  }

  /* ---------- Re-init JS bound to replaced DOM ---------- */
  function reinitAfterSwap() {
    var section = document.querySelector(SECTION_SELECTOR);

    /* Tell anything listening to shopify:section:load (e.g.
     * smart-filter-snowboard.js) to re-bind. */
    if (section) {
      var ev;
      try {
        ev = new CustomEvent('shopify:section:load', {
          bubbles: true,
          detail: { sectionId: section.getAttribute('data-section-id') || 'collection' }
        });
      } catch (e) {
        ev = document.createEvent('CustomEvent');
        ev.initCustomEvent('shopify:section:load', true, false,
          { sectionId: section.getAttribute('data-section-id') || 'collection' });
      }
      section.dispatchEvent(ev);
    }

    /* collection-filter-search.js is an IIFE with no event hook. Re-run
     * its script so its init() picks up the freshly-rendered typeahead
     * inputs. Re-running is safe: the IIFE only queries the current DOM. */
    rerunAsset('collection-filter-search.js');

    /* Re-bind the See-More toggle (the inline <script> at the bottom of
     * collection-template.liquid only runs at DOMContentLoaded). */
    rebindSeeMore();
  }

  function rerunAsset(filename) {
    var scripts = document.getElementsByTagName('script');
    for (var i = 0; i < scripts.length; i++) {
      var src = scripts[i].src || '';
      if (src.indexOf(filename) === -1) continue;
      var fresh = document.createElement('script');
      var clean = src.replace(/[?&]cajax=\d+/, '');
      fresh.src = clean + (clean.indexOf('?') === -1 ? '?' : '&') + 'cajax=' + Date.now();
      fresh.async = false;
      document.head.appendChild(fresh);
      return;
    }
  }

  function rebindSeeMore() {
    document.querySelectorAll('.seemore').forEach(function (btn) {
      if (btn.__seemoreBound) return;
      btn.__seemoreBound = true;
      var hiddenCount = btn.getAttribute('data-hidden-count') || '';
      var labelEl = btn.querySelector('.seemore__label');
      var iconEl  = btn.querySelector('.seemore__icon');
      var collapsedText = hiddenCount ? ('See ' + hiddenCount + ' more') : 'See more';
      var expandedText  = 'Show less';
      btn.setAttribute('aria-expanded', 'false');
      btn.addEventListener('click', function () {
        var list = btn.closest('ul');
        if (!list) return;
        var expanded = btn.getAttribute('aria-expanded') === 'true';
        list.querySelectorAll('[data-hidden-default]').forEach(function (li) {
          if (expanded) {
            li.classList.add('filter-item--hidden');
            li.classList.remove('filter-item--visible');
          } else {
            li.classList.remove('filter-item--hidden');
            li.classList.add('filter-item--visible');
          }
        });
        if (labelEl) labelEl.textContent = expanded ? collapsedText : expandedText;
        if (iconEl)  iconEl.classList.toggle('seemore__icon--up', !expanded);
        btn.setAttribute('aria-expanded', expanded ? 'false' : 'true');
      });
    });
  }

  /* ---------- Sort URL builder ---------- */
  function buildSortUrl(value) {
    var u = new URL(window.location.href);
    u.searchParams.set('sort_by', value);
    u.searchParams.delete('page');
    return u.pathname + u.search + u.hash;
  }

  /* ---------- Loading state ---------- */
  var progressTimer = null;
  var progress = 0;
  function setLoading(on) {
    var body = document.body;
    if (on) {
      body.setAttribute(BODY_LOADING_ATTR, '');
      progress = 0;
      body.style.setProperty(PROGRESS_VAR, '0%');
      var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduced) {
        body.style.setProperty(PROGRESS_VAR, '80%');
        return;
      }
      clearInterval(progressTimer);
      progressTimer = setInterval(function () {
        /* Asymptotic crawl to 80%. */
        progress += (80 - progress) * 0.12;
        body.style.setProperty(PROGRESS_VAR, progress.toFixed(1) + '%');
        if (progress > 79.5) clearInterval(progressTimer);
      }, 120);
    } else {
      clearInterval(progressTimer);
      progressTimer = null;
      body.style.setProperty(PROGRESS_VAR, '100%');
      setTimeout(function () {
        body.removeAttribute(BODY_LOADING_ATTR);
        body.style.setProperty(PROGRESS_VAR, '0%');
      }, 220);
    }
  }
})();
